import { convexClient } from "../config/convex";
import { api } from "../../convex/_generated/api";
import type { Expediente } from "./expedienteService";
import type { DenunciaPorZona } from "../components/MapaZonasDenuncias";
import { cacheService } from "./cacheService";
import { obtenerExpedientes } from "./expedienteService.convex";

export interface ExpedientesRecientes {
  modificadosUltimos7Dias: Expediente[];
  creadosUltimos7Dias: Expediente[];
  sinCambios7Dias: Expediente[];
  sinCambios30Dias: Expediente[];
}

type EstadisticasActividad = {
  porSemana: { semana: string; cantidad: number }[];
  porDia: { dia: string; cantidad: number }[];
  porHora: { hora: number; cantidad: number }[];
  usuariosMasActivos: { usuario: string; actividades: number }[];
  porcentajeDenunciasConFoto: number;
  tiempoRespuestaInicial: {
    tiempoPromedio: number;
    expedientesConRespuesta: number;
    expedientesSinRespuesta: number;
  };
  tiposBarreras: {
    tipo: string;
    cantidad: number;
    porcentaje: number;
    detalles: unknown[];
  }[];
};

function parseFechaExpediente(fechaStr: string): Date {
  try {
    if (fechaStr.includes("T")) return new Date(fechaStr);
    if (fechaStr.includes(",")) {
      const [fechaParte, horaParte] = fechaStr.split(",");
      const [dia, mes, anio] = fechaParte.trim().split("/").map(Number);
      const [horas, minutos, segundos] = horaParte.trim().split(":").map(Number);
      return new Date(anio, mes - 1, dia, horas, minutos, segundos);
    }
    return new Date(fechaStr);
  } catch {
    return new Date();
  }
}

/** Recorre el listado paginado existente (máx. ~500 por límite del backend). */
async function obtenerExpedientesParaFallback(): Promise<Expediente[]> {
  const pageSize = 25;
  const maxTotal = 500;
  const all: Expediente[] = [];
  for (let offset = 0; offset < maxTotal; offset += pageSize) {
    const page = await obtenerExpedientes(pageSize, offset);
    all.push(...page);
    if (page.length < pageSize) break;
  }
  return all;
}

function calcularEstadisticasDesdeExpedientes(
  expedientes: Expediente[],
  meses: number
): EstadisticasActividad {
  const fechaLimite = new Date();
  fechaLimite.setMonth(fechaLimite.getMonth() - meses);
  const filtered = expedientes.filter(
    (exp) => parseFechaExpediente(exp.fechaCreacion) >= fechaLimite
  );

  const porSemana: { semana: string; cantidad: number }[] = [];
  const semanas: Record<string, number> = {};
  filtered.forEach((exp) => {
    const fecha = parseFechaExpediente(exp.fechaCreacion);
    const primerDia = new Date(fecha.getFullYear(), 0, 1);
    const dias = Math.floor(
      (fecha.getTime() - primerDia.getTime()) / (24 * 60 * 60 * 1000)
    );
    const semana = Math.ceil((dias + primerDia.getDay() + 1) / 7);
    const clave = `${fecha.getFullYear()}-S${semana}`;
    semanas[clave] = (semanas[clave] ?? 0) + 1;
  });
  Object.entries(semanas).forEach(([semana, cantidad]) =>
    porSemana.push({ semana, cantidad })
  );
  porSemana.sort((a, b) => a.semana.localeCompare(b.semana));

  const diasSemana = [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ];
  const contadorDias: Record<string, number> = {};
  diasSemana.forEach((d) => {
    contadorDias[d] = 0;
  });
  filtered.forEach((exp) => {
    const dia = diasSemana[parseFechaExpediente(exp.fechaCreacion).getDay()];
    contadorDias[dia]++;
  });
  const porDia = diasSemana.map((dia) => ({ dia, cantidad: contadorDias[dia] }));

  const contadorHoras: Record<number, number> = {};
  for (let i = 0; i < 24; i++) contadorHoras[i] = 0;
  filtered.forEach((exp) => {
    const hora = parseFechaExpediente(exp.fechaCreacion).getHours();
    contadorHoras[hora]++;
  });
  const porHora = Object.entries(contadorHoras).map(([hora, cantidad]) => ({
    hora: parseInt(hora, 10),
    cantidad,
  }));

  const contadorUsuarios: Record<string, number> = {};
  filtered.forEach((exp) => {
    exp.historial?.forEach((c) => {
      if (c.usuario) contadorUsuarios[c.usuario] = (contadorUsuarios[c.usuario] ?? 0) + 1;
    });
    exp.comunicacionesInternas?.forEach((c) => {
      if (c.usuario) contadorUsuarios[c.usuario] = (contadorUsuarios[c.usuario] ?? 0) + 1;
    });
  });
  const usuariosMasActivos = Object.entries(contadorUsuarios)
    .map(([usuario, actividades]) => ({ usuario, actividades }))
    .sort((a, b) => b.actividades - a.actividades)
    .slice(0, 10);

  const conFoto = filtered.filter(
    (exp) =>
      (exp.imagenUrl?.trim() ?? "") !== "" || (exp.imagenBarrera?.trim() ?? "") !== ""
  ).length;
  const porcentajeDenunciasConFoto =
    filtered.length > 0 ? Math.round((conFoto / filtered.length) * 1000) / 10 : 0;

  let tiempoTotal = 0;
  let conRespuesta = 0;
  let sinRespuesta = 0;
  filtered.forEach((exp) => {
    if (exp.historial?.length) {
      const fechaCreacion = parseFechaExpediente(exp.fechaCreacion);
      const fechaRespuesta = parseFechaExpediente(exp.historial[0].fecha);
      tiempoTotal +=
        (fechaRespuesta.getTime() - fechaCreacion.getTime()) / (1000 * 60 * 60 * 24);
      conRespuesta++;
    } else sinRespuesta++;
  });

  const total = filtered.length;
  const byTipo: Record<string, number> = {};
  filtered.forEach((exp) => {
    const t = exp.tipoBarreraDetalle ?? "";
    const key = t.startsWith("urbanistica:")
      ? "Urbanística"
      : t.startsWith("edificacion:")
        ? "Edificación"
        : t.startsWith("transporte:")
          ? "Transporte"
          : "Sin clasificar";
    byTipo[key] = (byTipo[key] ?? 0) + 1;
  });
  const tiposBarreras = Object.entries(byTipo).map(([tipo, cantidad]) => ({
    tipo,
    cantidad,
    porcentaje: total > 0 ? Math.round((cantidad / total) * 1000) / 10 : 0,
    detalles: [] as unknown[],
  }));

  return {
    porSemana,
    porDia,
    porHora,
    usuariosMasActivos,
    porcentajeDenunciasConFoto,
    tiempoRespuestaInicial: {
      tiempoPromedio:
        conRespuesta > 0 ? Math.round((tiempoTotal / conRespuesta) * 10) / 10 : 0,
      expedientesConRespuesta: conRespuesta,
      expedientesSinRespuesta: sinRespuesta,
    },
    tiposBarreras,
  };
}

export async function obtenerEstadisticasActividad(
  meses: number = 6
): Promise<EstadisticasActividad> {
  const cacheKey = `estadisticas_actividad_${meses}`;
  const cached = cacheService.get<EstadisticasActividad>(cacheKey);
  if (cached) return cached;

  try {
    const result = await convexClient.action(
      api.estadisticasActividad.getActivityStats,
      {
        meses,
        nowMs: Date.now(),
      }
    );

    if (result.totalEscaneados >= 10000) {
      console.warn(
        "Estadísticas: se alcanzó el límite de escaneo de expedientes; el total podría estar incompleto."
      );
    }

    const mapped: EstadisticasActividad = {
      porSemana: result.porSemana,
      porDia: result.porDia,
      porHora: result.porHora,
      usuariosMasActivos: result.usuariosMasActivos,
      porcentajeDenunciasConFoto: result.porcentajeDenunciasConFoto,
      tiempoRespuestaInicial: result.tiempoRespuestaInicial,
      tiposBarreras: result.tiposBarreras,
    };

    cacheService.set(cacheKey, mapped, 15 * 60 * 1000);
    return mapped;
  } catch (error) {
    console.warn(
      "Action de estadísticas no disponible; usando listado paginado como respaldo:",
      error
    );
    const expedientes = await obtenerExpedientesParaFallback();
    const mapped = calcularEstadisticasDesdeExpedientes(expedientes, meses);
    cacheService.set(cacheKey, mapped, 15 * 60 * 1000);
    return mapped;
  }
}

export async function obtenerExpedientesRecientes(): Promise<ExpedientesRecientes> {
  const cacheKey = "expedientes_recientes";
  const cached = cacheService.get<ExpedientesRecientes>(cacheKey);
  if (cached) return cached;
  const expedientes = await obtenerExpedientes(25);
  const hace7 = new Date();
  hace7.setDate(hace7.getDate() - 7);
  const hace30 = new Date();
  hace30.setDate(hace30.getDate() - 30);

  const modificadosUltimos7Dias = expedientes.filter((exp) => {
    if (!exp.historial?.length) return false;
    const ultima = exp.historial.sort(
      (a, b) =>
        parseFechaExpediente(b.fecha).getTime() - parseFechaExpediente(a.fecha).getTime()
    )[0];
    return parseFechaExpediente(ultima.fecha) >= hace7;
  });

  const creadosUltimos7Dias = expedientes.filter(
    (exp) => parseFechaExpediente(exp.fechaCreacion) >= hace7
  );

  const idsModificados = new Set(modificadosUltimos7Dias.map((e) => e.id));
  const sinCambios7Dias = expedientes.filter((exp) => {
    if (exp.estado === "Resuelto" || idsModificados.has(exp.id)) return false;
    if (!exp.historial?.length)
      return parseFechaExpediente(exp.fechaCreacion) < hace7;
    const ultima = exp.historial.sort(
      (a, b) =>
        parseFechaExpediente(b.fecha).getTime() - parseFechaExpediente(a.fecha).getTime()
    )[0];
    return parseFechaExpediente(ultima.fecha) < hace7;
  });

  const sinCambios30Dias = expedientes.filter((exp) => {
    if (exp.estado === "Resuelto" || idsModificados.has(exp.id)) return false;
    if (!exp.historial?.length)
      return parseFechaExpediente(exp.fechaCreacion) < hace30;
    const ultima = exp.historial.sort(
      (a, b) =>
        parseFechaExpediente(b.fecha).getTime() - parseFechaExpediente(a.fecha).getTime()
    )[0];
    return parseFechaExpediente(ultima.fecha) < hace30;
  });

  const result = {
    modificadosUltimos7Dias,
    creadosUltimos7Dias,
    sinCambios7Dias,
    sinCambios30Dias,
  };
  cacheService.set(cacheKey, result, 5 * 60 * 1000);
  return result;
}

export async function obtenerDenunciasPorZona(
  meses: number = 6
): Promise<DenunciaPorZona[]> {
  const cacheKey = `denuncias_por_zona_${meses}`;
  const cached = cacheService.get<DenunciaPorZona[]>(cacheKey);
  if (cached) return cached;

  try {
    const result = await convexClient.action(
      api.estadisticasActividad.getDenunciasPorZona,
      {
        meses,
        nowMs: Date.now(),
      }
    );
    cacheService.set(cacheKey, result, 10 * 60 * 1000);
    return result;
  } catch (error) {
    console.warn(
      "Action de denuncias por zona no disponible; usando listado paginado:",
      error
    );
    const fechaLimite = new Date();
    fechaLimite.setMonth(fechaLimite.getMonth() - meses);
    const expedientes = await obtenerExpedientesParaFallback();
    const result: DenunciaPorZona[] = expedientes
      .filter((exp) => parseFechaExpediente(exp.fechaCreacion) >= fechaLimite)
      .map((exp) => ({
        id: exp.id,
        direccion: exp.direccion ?? "Dirección no disponible",
        localidad: exp.localidad ?? "Localidad no disponible",
        provincia: exp.provincia ?? "Provincia no disponible",
        codigoPostal: exp.codigoPostal ?? "00000",
        estado: exp.estado ?? "En trámite",
      }));
    cacheService.set(cacheKey, result, 10 * 60 * 1000);
    return result;
  }
}
